import React, { useEffect, useState } from 'react';
import { Paper, Grid, Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BalanceIcon from '@mui/icons-material/Balance';
import InventoryIcon from '@mui/icons-material/Inventory';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import JournalEntries from './JournalEntries';
import BankReconciliation from './BankReconciliation';
import ChartOfAccounts from './ChartOfAccounts';
import TrialBalance from './TrialBalance';
import AccountLedger from './AccountLedger';
import BalanceSheet from './BalanceSheet';
import FinancialStatements from './FinancialStatements';
import YearEndClose from './YearEndClose';
import FixedAssets from './FixedAssets';
import StatCard from '../StatCard';
import EmptyState from '../EmptyState';
import { formatMoney } from '../../utils/money';


// The presets people actually ask for. A free date range is available underneath; these
// exist so the common questions do not require picking two dates.
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = (back = 0) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - back);
  return d.toISOString().slice(0, 10);
};
const monthEndISO = (back = 0) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - back + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
};

const AccountingDashboard = () => {
  const [data, setData] = useState(null);
  // Empty means "the financial year to date", which the server decides — the fiscal year
  // start is a setting, not the calendar, and the client has no business guessing it.
  const [range, setRange] = useState({ from: '', to: '' });
  const [showJournals, setShowJournals] = useState(false);
  const [showBankRec, setShowBankRec] = useState(false);
  const [showCOA, setShowCOA] = useState(false);
  const [showTrial, setShowTrial] = useState(false);

  // The account whose ledger is open, or '' for the ledger opened with nothing chosen yet.
  // null means the dialog is closed — distinct from '', which is open and waiting for a
  // pick, and a plain boolean could not tell those two apart.
  const [ledgerAccountId, setLedgerAccountId] = useState(null);

  // Drilling in from another report. The two screens that show a balance close as this
  // opens, so the ledger is not buried behind the dialog that sent you to it — and each
  // passes whatever it calls the account's id, because the trial balance row says
  // `accountId` and a chart of accounts row says `_id`.
  const openLedgerFor = (accountId) => {
    setShowCOA(false);
    setShowTrial(false);
    setLedgerAccountId(accountId || '');
  };
  const [showBalanceSheet, setShowBalanceSheet] = useState(false);
  // null = closed; 0/1/2 = which statement tab to open in FinancialStatements.
  const [statementsTab, setStatementsTab] = useState(null);
  const [showYearEnd, setShowYearEnd] = useState(false);
  const [showAssets, setShowAssets] = useState(false);

  const fetchData = async (window = range) => {
    try {
      const params = {};
      if (window.from) params.from = window.from;
      if (window.to) params.to = window.to;
      const res = await axios.get('/accounting/dashboard', { params });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load accounting dashboard');
    }
  };

  // Once, on mount. Every later load goes through applyRange, so fetchData is deliberately
  // not a dependency — listing it would re-fetch on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const applyRange = (next) => {
    setRange(next);
    fetchData(next);
  };

  if (!data) return null;

  const { totals, counts, recentTransactions, currency, integrity, period } = data;

  const periodLabel = period
    ? (period.label || `${new Date(period.from).toLocaleDateString()} – ${new Date(period.to).toLocaleDateString()}`)
    : '';

  return (
    <Box>
      {/* WHICH FIGURES THE DATES BELOW APPLY TO.
          A position and a flow answer different questions, and a date range means something
          different to each. Assets, liabilities, equity and Cash on Hand are what is true AS
          AT today; narrowing those to a month would answer nothing anybody asks. The five
          below the divider are what happened DURING the period, and stating it is the
          difference between a figure being read and a figure being misread. */}
      <Box mb={2} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Period:</Typography>
        <Button size="small" variant={period?.isDefault ? 'contained' : 'outlined'}
          onClick={() => applyRange({ from: '', to: '' })}>Financial year to date</Button>
        <Button size="small" variant="outlined"
          onClick={() => applyRange({ from: monthStartISO(0), to: todayISO() })}>This month</Button>
        <Button size="small" variant="outlined"
          onClick={() => applyRange({ from: monthStartISO(1), to: monthEndISO(1) })}>Last month</Button>
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
          value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
          value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
        <Button size="small" variant="outlined" onClick={() => applyRange(range)}
          disabled={!range.from && !range.to}>Apply</Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><StatCard label="Total Assets" value={formatMoney(totals.assets, currency)} tone="neutral" /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard label="Total Liabilities" value={formatMoney(totals.liabilities, currency)} tone="caution" /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard label="Total Equity" value={formatMoney(totals.equity, currency)} tone="info" /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard label={`Net Profit (${periodLabel})`} value={formatMoney(totals.netProfitYtd, currency)} tone="positive" /></Grid>

        <Grid item xs={12} sm={6} md={3}><StatCard label="Journal Entries" value={counts.journalEntries} tone="neutral" /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard label="Active Accounts" value={counts.activeAccounts} tone="neutral" /></Grid>
        {/* GROSS movement on the cash, bank and mobile money accounts for the year --
            every debit one side, every credit the other. A cancelled receipt does not leave
            the first figure; it adds to the second. So these two are a pair that reconciles
            against the cash flow report, and their difference is not a balance. Labelled
            "gross" because reading them as net is exactly the mistake they invite. */}
        <Grid item xs={12} sm={6} md={3}><StatCard label={`Money In (gross, ${periodLabel})`} value={formatMoney(totals.receipts, currency)} tone="positive" /></Grid>
        <Grid item xs={12} sm={6} md={3}><StatCard label={`Money Out (gross, ${periodLabel})`} value={formatMoney(totals.payments, currency)} tone="caution" /></Grid>
        {/* The control. Every money account's balance, opening balances included, which
            makes it the one figure here that can be checked against a bank statement.
            Nothing else on this dashboard can be compared to anything outside the system --
            which is how 6,095,000 of cash that was not in the bank sat here unnoticed while
            every other tile agreed with every other tile. */}
        <Grid item xs={12} sm={6} md={3}><StatCard label="Cash on Hand" value={formatMoney(totals.cashOnHand, currency)} tone="info" /></Grid>
        {/* What came in and what it cost, which is what the gross pair above is NOT.
            Received reads the payments table, so a cancelled receipt is simply not one --
            it is not Money In with reversals subtracted, and will not tie to it.
            Spent is movement on expense accounts: the agency's own costs. Money Out includes
            landlord payouts and cancelled receipts flowing back out, and neither is a cost
            to the agency. */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label={`Received (${periodLabel})`} value={formatMoney(totals.received, currency)} tone="positive" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label={`Spent (${periodLabel})`} value={formatMoney(totals.spent, currency)} tone="caution" />
        </Grid>
      </Grid>

      {/* A cancelled payment or invoice whose entries do not net to zero is a reversal
          that was never written. It does NOT unbalance the books -- both sides are wrong by
          the same amount -- so the balance check cannot see it, and nor can any total above.
          Surfaced here because the only cost of missing it is finding out months later. */}
      {integrity && integrity.sources > 0 && (
        <Box mt={3} p={2} sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1, bgcolor: 'warning.light' }}>
          <Typography variant="subtitle2" gutterBottom>
            {integrity.sources} cancelled record{integrity.sources === 1 ? '' : 's'} did not fully reverse
          </Typography>
          <Typography variant="body2">
            {integrity.strandedValue > 0
              ? `The money accounts are overstated by ${formatMoney(integrity.strandedValue, currency)} — cash the books claim and the bank does not have.`
              : 'No money account is affected, but entries are still outstanding.'}
            {' '}Run Data Correction → repair-cancelled-reversals (dry run first).
          </Typography>
        </Box>
      )}

      {/* A revenue account carrying a DEBIT balance, or a cost account carrying a credit.
          Neither the balance check nor the cancellation check can see this — the books
          balance to the shilling and nothing is unreversed — yet it is exactly what a
          correction dated apart from the entries it corrects leaves behind. 4000 sat in
          debit by 2,750,000 for days while every other figure on this screen agreed with
          every other figure. */}
      {integrity && integrity.againstNature && integrity.againstNature.length > 0 && (
        <Box mt={3} p={2} sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1, bgcolor: 'warning.light' }}>
          <Typography variant="subtitle2" gutterBottom>
            {integrity.againstNature.length} account{integrity.againstNature.length === 1 ? '' : 's'} on the wrong side of themselves
          </Typography>
          {integrity.againstNature.map((a) => (
            <Typography key={a.code} variant="body2">
              {a.code} {a.name} — {a.type === 'income' ? 'revenue account in debit' : 'cost account in credit'} by {formatMoney(a.amount, currency)}
            </Typography>
          ))}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Usually a correction dated apart from the entries it corrects. The books still
            balance, so nothing else will report it.
          </Typography>
        </Box>
      )}

      {/* A record and its own entry disagreeing. The three checks above all read the ledger
          and ask whether it is coherent with itself; this is the only one that looks back at
          the record the entry was posted from, so it is the only one that can see the two
          drift apart. The lock on a posted expense's amount had a bug that let exactly this
          happen, and nothing on this screen would have shown it. */}
      {integrity && integrity.sourceMismatches && integrity.sourceMismatches.length > 0 && (
        <Box mt={3} p={2} sx={{ border: '1px solid', borderColor: 'error.main', borderRadius: 1, bgcolor: 'error.light' }}>
          <Typography variant="subtitle2" gutterBottom>
            {integrity.sourceMismatches.length} record{integrity.sourceMismatches.length === 1 ? '' : 's'} disagree{integrity.sourceMismatches.length === 1 ? 's' : ''} with the entry it posted
          </Typography>
          {integrity.sourceMismatches.map((m) => (
            <Typography key={`${m.type}-${m.id}`} variant="body2">
              {m.type} {m.reference ? `"${m.reference}"` : m.id.slice(-6)} — recorded as{' '}
              {formatMoney(m.recorded, currency)}, posted as {formatMoney(m.posted, currency)}
              {' '}({m.difference > 0 ? 'ledger understated' : 'ledger overstated'} by{' '}
              {formatMoney(Math.abs(m.difference), currency)})
            </Typography>
          ))}
          <Typography variant="body2" sx={{ mt: 1 }}>
            The ledger figure is what every report and statement uses. Reverse the record and
            approve it again to re-post it from its current values.
          </Typography>
        </Box>
      )}

      <Box mt={3}>
        <Typography variant="h6" gutterBottom>Recent Transactions</Typography>
        <Paper variant="outlined">
          {recentTransactions.length === 0 ? (
            <EmptyState compact title="No transactions yet" />
          ) : (
            <>
              <Box px={2} py={1} sx={{ display: 'flex', fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem' }}>
                <Box flex={1}>Date</Box>
                <Box flex={2}>Memo</Box>
              </Box>
              {recentTransactions.map((j) => (
                <Box key={j._id} px={2} py={1} sx={{ display: 'flex', borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box flex={1}>{new Date(j.entryDate).toLocaleDateString()}</Box>
                  <Box flex={2}>{j.memo || 'Journal Entry'}</Box>
                </Box>
              ))}
            </>
          )}
        </Paper>
      </Box>

      <Box mt={3} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<MenuBookIcon />} onClick={() => setShowJournals(true)}>Journal Entries</Button>
        <Button variant="outlined" startIcon={<AccountBalanceIcon />} onClick={() => setShowBankRec(true)}>Bank Reconciliation</Button>
        <Button variant="outlined" startIcon={<ListAltIcon />} onClick={()=>setShowCOA(true)}>Chart of Accounts</Button>
        <Button variant="outlined" startIcon={<BalanceIcon />} onClick={()=>setShowTrial(true)}>Trial Balance</Button>
        <Button variant="outlined" startIcon={<MenuBookIcon />} onClick={()=>setLedgerAccountId('')}>Account Ledger</Button>
        {/* IAS 1 / IAS 7 primary statements — each opens the set on the matching tab. */}
        <Button variant="contained" startIcon={<TrendingUpIcon />} onClick={()=>setStatementsTab(0)}>
          Profit or Loss and OCI
        </Button>
        <Button variant="contained" startIcon={<AccountTreeIcon />} onClick={()=>setStatementsTab(1)}>
          Changes in Equity
        </Button>
        <Button variant="contained" startIcon={<WaterfallChartIcon />} onClick={()=>setStatementsTab(2)}>
          Cash Flow Statement
        </Button>
        <Button variant="contained" startIcon={<AccountBalanceIcon />} onClick={()=>setStatementsTab(3)}>
          Financial Position
        </Button>
        <Button variant="outlined" startIcon={<AccountBalanceIcon />} onClick={()=>setShowBalanceSheet(true)}>
          Balance Sheet (detail)
        </Button>
        <Button variant="outlined" startIcon={<InventoryIcon />} onClick={()=>setShowAssets(true)}>Fixed Assets</Button>
        <Button variant="outlined" startIcon={<EventAvailableIcon />} onClick={()=>setShowYearEnd(true)}>Year End</Button>
      </Box>

      <Dialog open={showJournals} onClose={() => setShowJournals(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Journal Entries
          <IconButton onClick={() => setShowJournals(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <JournalEntries />
        </DialogContent>
      </Dialog>
      <Dialog open={showBankRec} onClose={() => setShowBankRec(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Bank Reconciliation
          <IconButton onClick={() => setShowBankRec(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <BankReconciliation />
        </DialogContent>
      </Dialog>
      <Dialog open={showCOA} onClose={()=>setShowCOA(false)} maxWidth='xl' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Chart of Accounts
          <IconButton onClick={() => setShowCOA(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <ChartOfAccounts onDrillDown={openLedgerFor} />
        </DialogContent>
      </Dialog>
      {/* Keyed on the account so switching from one drill-down to another remounts the
          screen rather than leaving the previous account's rows under a new heading. */}
      <Dialog open={ledgerAccountId !== null} onClose={()=>setLedgerAccountId(null)} maxWidth='xl' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Account Ledger
          <IconButton onClick={() => setLedgerAccountId(null)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <AccountLedger key={ledgerAccountId || 'none'} accountId={ledgerAccountId || ''} />
        </DialogContent>
      </Dialog>
      <Dialog open={showTrial} onClose={()=>setShowTrial(false)} maxWidth='lg' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Trial Balance
          <IconButton onClick={() => setShowTrial(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TrialBalance onDrillDown={openLedgerFor} />
        </DialogContent>
      </Dialog>
      <Dialog open={showBalanceSheet} onClose={()=>setShowBalanceSheet(false)} maxWidth='lg' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Balance Sheet
          <IconButton onClick={() => setShowBalanceSheet(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <BalanceSheet />
        </DialogContent>
      </Dialog>
      <Dialog open={statementsTab !== null} onClose={()=>setStatementsTab(null)} maxWidth='xl' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Financial Statements
          <IconButton onClick={() => setStatementsTab(null)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Keyed so opening a different statement remounts on the right tab. */}
          {statementsTab !== null && (
            <FinancialStatements key={statementsTab} initialTab={statementsTab} />
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={showAssets} onClose={()=>setShowAssets(false)} maxWidth='xl' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Fixed Assets
          <IconButton onClick={() => setShowAssets(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <FixedAssets />
        </DialogContent>
      </Dialog>
      <Dialog open={showYearEnd} onClose={()=>setShowYearEnd(false)} maxWidth='lg' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Year End
          <IconButton onClick={() => setShowYearEnd(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <YearEndClose />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AccountingDashboard;


